import { useState } from "react";

import MatterNotes from "@/components/features/matter/notes/MatterNotes";
import {
  Card,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { useTasksViewModel } from "@/hooks/matter-view-models/use-tasks-view-model";
import type { Matter } from "@/lib/types/Matter";
import { EventLogs } from "../event-logs/EventLogs";
import MatterTasks from "../tasks/MatterTasks";

type MatterSidebarTab = "notes" | "tasks" | "eventlog";

export const MatterOverViewNotesTasksEventsSidebar = ({
  matter,
  massTortId,
  activeTab: activeTabProp,
  onTabChange,
  hideTabs = false,
}: {
  matter: Matter;
  massTortId?: string;
  activeTab?: MatterSidebarTab;
  onTabChange?: (tab: MatterSidebarTab) => void;
  hideTabs?: boolean;
}) => {
  const [internalTab, setInternalTab] = useState<MatterSidebarTab>("notes");
  const tab = activeTabProp ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;

  const { allTasks } = useTasksViewModel(massTortId ? undefined : matter);

  const content = (
    <div className="flex h-full flex-col">
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as MatterSidebarTab);
        }}
        className="flex h-full flex-col"
      >
        {!hideTabs && (
          <TabsList className="mb-2 w-full justify-start">
            <TabsTrigger data-testid="notes-tab" value="notes">
              Notes
            </TabsTrigger>
            <TabsTrigger data-testid="tasks-tab" value="tasks">
              Tasks
            </TabsTrigger>
            <TabsTrigger data-testid="eventlog-tab" value="eventlog">
              Event log
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="notes" className="mt-0 h-full overflow-hidden">
          <div className="h-full overflow-y-auto pr-2">
            <MatterNotes
              notes={[]}
              matter={matter}
              massTortId={massTortId}
              variant="eventlog"
              bareLayout={hideTabs}
              showAnimation
              showComposerOverride
              showComments
            />
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-0 h-full overflow-hidden">
          <div className="h-full overflow-y-auto">
            <MatterTasks
              tasks={allTasks}
              matter={matter}
              massTortId={massTortId}
              showCreateButton
              shrinkCreateButton
              defaultVariant="eventlog"
              bareLayout={hideTabs}
              showAnimation
              filterSelectorFullWidth
              showQuickFilters={false}
            />
          </div>
        </TabsContent>

        <TabsContent value="eventlog" className="mt-0 h-full overflow-hidden">
          <EventLogs
            matter={matter}
            massTortId={massTortId}
            bareLayout={hideTabs}
          />
        </TabsContent>
      </Tabs>
    </div>
  );

  if (hideTabs) {
    return <div className="flex h-full flex-col px-4 pt-4 pb-4">{content}</div>;
  }

  return (
    <Card className="col-span-full h-full rounded-none border-none p-4 lg:col-span-4">
      {content}
    </Card>
  );
};
